import { onIdle } from './on-idle'

// Cache the visitor-id computation so the FingerprintJS agent loads and runs
// once per session; subsequent callers reuse the same promise.
let deviceIdPromise: Promise<string> | null = null

// Upper bound on how long we wait for the fingerprint. Without it, a
// FingerprintJS call that neither resolves nor rejects (browser quirk / hang)
// would leave `await getDeviceId()` pending forever and lock the register form
// in its loading state. On timeout we fall back to an empty id.
const FINGERPRINT_TIMEOUT_MS = 5000

const timeout = (ms: number): Promise<string> =>
  new Promise(resolve => setTimeout(() => resolve(''), ms))

const computeDeviceId = async (): Promise<string> => {
  try {
    return await Promise.race([
      (async () => {
        // Dynamic import keeps the ~125KB FingerprintJS bundle out of the entry
        // chunk; it is fetched only when a device id is actually needed
        // (register / deposit submit, or the idle warm-up below).
        const { default: FingerprintJS } = await import(
          '@fingerprintjs/fingerprintjs'
        )
        const agent = await FingerprintJS.load()
        const result = await agent.get()
        return result.visitorId
      })(),
      timeout(FINGERPRINT_TIMEOUT_MS),
    ])
  } catch (error) {
    return ''
  }
}

export const getDeviceId = (): Promise<string> => {
  if (!deviceIdPromise) {
    deviceIdPromise = computeDeviceId()
  }
  return deviceIdPromise
}

// Fire-and-forget warm-up so the visitor id is ready by the time it is needed
// (e.g. on register submit), avoiding latency on the user-facing action.
//
// FingerprintJS (canvas/audio/font probing) is heavy synchronous main-thread
// work. Running it during initial bundle execution stole time from the first
// render and inflated FCP/LCP render delay. Defer the warm-up to idle (via the
// shared onIdle) so it no longer competes with the critical render —
// `getDeviceId()` still creates the promise on demand if the register form
// needs it before the idle callback fires.
//
// The 1500ms fallbackDelayMs (vs onIdle's near-immediate default) only applies
// on browsers without requestIdleCallback (Safari < 16.4): there, a next-tick
// fallback would run this heavy work right against first render, so it is
// pushed out further.
export const preloadDeviceId = () => {
  onIdle(() => void getDeviceId(), { fallbackDelayMs: 1500 })
}
